import { useState, useRef, useCallback, useContext } from 'react';
import { Check, X } from 'react-feather';
import * as Table from '../Table';
import styles from './styles/Draggable.module.css';
import Input from '../Input';
import Pencil from '../icons/Pencil';
import { NavigationContext } from '@trinity/chat-components/containers/Chat/RoutingProvider';

type Props = {
  editable: boolean;
  isValid: (val: string) => boolean;
  onSubmit: (val: string) => void;
  value: string;
  tagId: number;
  getTagDetailUrl: (tagId: number) => string;
  typeId?: number;
};

export const Cell = (props: Props) => {
  const navigate = useContext(NavigationContext);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [canSave, setCanSave] = useState(false);

  const handleTitleClick = () => {
    if (!navigate) return;
    navigate(props.getTagDetailUrl(props.tagId));
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation when clicking edit icon
    setIsEditing(true);
  };

  const recalcSave = useCallback(() => {
    if (!inputRef.current) return;
    const isValid =
      inputRef.current.value !== props.value && (!props.isValid || props.isValid(inputRef.current.value));
    setCanSave(isValid);
  }, [props.isValid, props.value]);

  const saveValue = useCallback(() => {
    if (inputRef.current && canSave) {
      props.onSubmit(inputRef.current.value);
      setIsEditing(false);
    }
  }, [props.onSubmit, canSave]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    setCanSave(false);
  }, []);

  return (
    <Table.Body.TD className={styles.title}>
      {isEditing ? (
        <div className={styles.editingContainer}>
          <Input
            classes={{
              input: styles.inputElement,
            }}
            ref={inputRef}
            defaultValue={props.value}
            onChange={recalcSave}
            autoFocus
          />
          <Check
            className={canSave ? styles.enabled : styles.disabled}
            onClick={canSave ? saveValue : undefined}
            size={16}
          />
          <X className={styles.cancel} onClick={cancelEdit} size={16} />
        </div>
      ) : (
        <div className={styles.titleContainer}>
          <button className={styles.titleButton} onClick={handleTitleClick}>
            {props.value}
          </button>
          {props.editable && (
            <div className={styles.editIcon} onClick={handleEditClick}>
              <Pencil size={14} />
            </div>
          )}
        </div>
      )}
    </Table.Body.TD>
  );
};

Cell.displayName = 'Column.Title.Cell';

export const Header = (props: unknown) => {
  return <Table.Header.TH className={styles.title}>Tag Title</Table.Header.TH>;
};

Header.displayName = 'Column.Title.Header';
